import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2BCatalogApi from 'api/b2b/CatalogApiB2b'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import CatalogGroupListTable from './CatalogGroupList'

// Updated interface to match actual API response
interface CatalogGroup {
  catalog_group_id: string
  catalog_group_name: string
  catalog_count: number
  company_assigned_to: string
  status: boolean
  created_by: string
  created_on: string
  updated_by: string
  updated_on: string
}

interface CatalogGroupOption {
  label: string
  value: string
  id: string
  fullData: CatalogGroup // Store complete catalog group data
}

interface FormData {
  catalogGroupName: CatalogGroupOption | null
}

function SearchCatalogGroup() {
  const navigate = useNavigate()
  const [tableData, setTableData] = useState<CatalogGroup[]>([])
  const [searchInputValue, setSearchInputValue] = useState('')
  const [selectedCatalogGroup, setSelectedCatalogGroup] =
    useState<CatalogGroup | null>(null)
  const [isOptionSelected, setIsOptionSelected] = useState(false)
  const token = OrbitSession.get('faberToken')

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: { catalogGroupName: null }
  })

  // Watch the selected option
  const catalogGroupName = watch('catalogGroupName')

  // Debounce the search input
  const debouncedSearchTerm = useDebounce(searchInputValue, 500)

  // Query to fetch catalog groups for dropdown (with search)
  const {
    data: catalogGroupOptions,
    isLoading: isLoadingOptions
    // refetch: refetchOptions
  } = useQuery<CatalogGroupOption[]>(
    ['catalog-groups', debouncedSearchTerm],
    async () => {
      if (debouncedSearchTerm.length < 2 || isOptionSelected) return []

      const response = await B2BCatalogApi.getCatalogGroupGrid(
        token,
        debouncedSearchTerm
      )

      let catalogGroupsData: CatalogGroup[] | null = null

      if (response?.data?.data && Array.isArray(response.data.data)) {
        catalogGroupsData = response.data.data
      } else if (response?.data && Array.isArray(response.data)) {
        catalogGroupsData = response.data
      } else if (response && Array.isArray(response)) {
        catalogGroupsData = response
      }

      if (catalogGroupsData && Array.isArray(catalogGroupsData)) {
        const options = catalogGroupsData.map((group: CatalogGroup) => ({
          label: group.catalog_group_name,
          value: group.catalog_group_name,
          id: group.catalog_group_id,
          fullData: group
        }))
        return options
      }

      return []
    },
    {
      enabled: debouncedSearchTerm.length >= 2 && !isOptionSelected,
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false
    }
  )

  // Mutation for getting detailed data when search is clicked
  const { isLoading: isSearching } = useMutation({
    mutationFn: async (catalogGroup: CatalogGroup) => {
      return [catalogGroup]
    },
    onSuccess: (data: CatalogGroup[]) => {
      setTableData(data)
      toast.success('Catalog group data loaded successfully')
    },
    onError: () => {
      setTableData([])
      toast.error('Failed to load catalog group details')
    }
  })

  // Mutation for getting all catalog groups
  const {
    mutate: fetchAllCatalogGroups,
    isLoading: isLoadingAllCatalogGroups
  } = useMutation({
    mutationFn: async () => {
      const response = await B2BCatalogApi.getCatalogGroupGrid(token)
      return response.data
    },
    onSuccess: (data: CatalogGroup[]) => {
      console.log('All catalog groups data:', data)
      setTableData(data)
    },
    onError: () => {
      setTableData([])
      toast.error('Failed to load catalog group details', {
        hideProgressBar: true,
        autoClose: 2000
      })
    }
  })

  // Handle input change for autocomplete
  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      if (value !== searchInputValue) {
        setIsOptionSelected(false)
      }
      setSearchInputValue(value)
    },
    [searchInputValue]
  )

  // Handle option selection - store selected catalog group
  const handleCatalogGroupSelect = useCallback(
    (option: CatalogGroupOption | null) => {
      if (option) {
        setSelectedCatalogGroup(option.fullData)
        setIsOptionSelected(true) // Mark that an option was selected
      } else {
        setSelectedCatalogGroup(null)
        setIsOptionSelected(false)
        setTableData([]) // Clear table when no option is selected
      }
    },
    []
  )

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!data.catalogGroupName) {
        toast.warning('Please select a catalog group')
        return
      }

      if (!selectedCatalogGroup) {
        toast.warning('No catalog group selected')
        return
      }

      // Display only the selected catalog group in table
      setTableData([selectedCatalogGroup])
      toast.success('Catalog group data loaded successfully')
    },
    [selectedCatalogGroup]
  )

  // Handle reset
  const handleReset = useCallback(() => {
    reset({ catalogGroupName: null })
    setTableData([])
    setSearchInputValue('')
    setSelectedCatalogGroup(null)
    setIsOptionSelected(false)
  }, [reset])

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end' sx={{ mb: 2 }}>
        <Button
          variant='contained'
          onClick={() => navigate('add')}
          disabled={isSearching}
        >
          Add Catalog Group
        </Button>
      </Box>

      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: '10px', fontSize: '14px', minWidth: '140px' }}>
            Catalog Group Name*
          </Typography>
          <Controller
            name='catalogGroupName'
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Please select a catalog group'
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                options={catalogGroupOptions || []}
                loading={isLoadingOptions}
                disablePortal
                size='small'
                sx={{ minWidth: '300px' }}
                getOptionLabel={(option: CatalogGroupOption) =>
                  option.label || ''
                }
                isOptionEqualToValue={(
                  option: CatalogGroupOption,
                  value: CatalogGroupOption | null
                ) => option?.id === value?.id}
                onChange={(_, data: CatalogGroupOption | null) => {
                  field.onChange(data)
                  handleCatalogGroupSelect(data)
                }}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Type to search catalog groups...'
                    error={!!error}
                    helperText={
                      error?.message ||
                      (searchInputValue.length > 0 &&
                      searchInputValue.length < 2
                        ? 'Type at least 2 characters to search'
                        : '')
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingOptions &&
                          debouncedSearchTerm.length >= 2 &&
                          !isOptionSelected ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Box>
                      <Typography variant='body2' fontWeight='medium'>
                        {option.label}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  searchInputValue.length < 2
                    ? 'Type at least 2 characters to search'
                    : isLoadingOptions
                    ? 'Searching...'
                    : 'No catalog groups found'
                }
              />
            )}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant='contained'
            type='submit'
            disabled={!catalogGroupName}
          >
            Search
          </Button>
          <Button variant='outlined' type='button' onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      <Button
        variant='outlined'
        type='button'
        onClick={() => fetchAllCatalogGroups()}
        disabled={isLoadingAllCatalogGroups}
      >
        Get All Catalog Groups
      </Button>
      <Box>
        <CatalogGroupListTable
          catalogGroupData={tableData}
          setTableData={setTableData}
          // isLoading={isLoadingOptions}
        />
      </Box>
    </Box>
  )
}

export default SearchCatalogGroup
